import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import type { FileWithPath } from 'react-dropzone';
import { toast } from 'react-toastify';

import { Button, Col, Flex, Row, Spin, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import uploadService from 'v2/services/upload';

import { FormContent } from '../../EditProduct/components/FormContent';

interface IProps {
  onUpload: (upload_id: number) => void;
  onRemove: () => void;
}

export const PrivateLabelRights = ({ onUpload, onRemove }: IProps) => {
  const [file, setFile] = useState<FileWithPath>();
  const [uploading, setUploading] = useState<boolean>(false);

  const onDrop = (files: FileWithPath[]) => {
    const f = [...files].shift();
    onUploadFile(f);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false
  });

  const onUploadFile = useCallback(
    (file: FileWithPath) => {
      setUploading(true);
      uploadService
        .uploadFile(file, 'private_label_rights')
        .then((response: { data: { upload_id: number } }) => {
          setFile(file);
          onUpload(response.data.upload_id);
          setUploading(false);
        })
        .catch(() => {
          toast.error('Falha ao subir arquivo, tente novamente');
          setFile(null);
          setUploading(false);
        });
    },
    [onUpload]
  );

  const handleRemove = () => {
    setFile(null);
    onRemove();
  };

  return (
    <FormContent title='Direitos privados de etiqueta' loading={false}>
      <Row gutter={16}>
        <Col span={24}>
          <Typography.Paragraph>
            Caso o conteúdo não seja de sua autoria, é necessário preencher&nbsp;
            <Typography.Link
              href='https://drive.google.com/file/d/0B49AZsMzd09sU3YxU3BXYUliZGc/view?resourcekey=0-3uLUHC6Oatpm7UguZ1kN3A'
              target='_blank'
              rel='noopener noreferrer'
            >
              este documento
            </Typography.Link>
            &nbsp; e anexá-lo no campo abaixo. Se você for o autor deste conteúdo, pode pular esta etapa.
          </Typography.Paragraph>

          <Typography.Title level={5}>Por que preciso fazer isso?</Typography.Title>
          <Typography.Paragraph>
            Confira&nbsp;
            <Typography.Link
              href='https://ajuda.eduzz.com/hc/pt-br/articles/8004679566107-o-que-%C3%A9-e-como-cadastrar-meu-produto-plr-na-eduzz'
              target='_blank'
              rel='noopener noreferrer'
            >
              este artigo
            </Typography.Link>
            &nbsp;para entender todos os detalhes.
          </Typography.Paragraph>
        </Col>

        <Col span={24}>
          {!uploading && file && (
            <>
              <Typography.Title level={5}>Arquivo</Typography.Title>
              <Flex
                align='center'
                justify='space-between'
                className='rounded-md border border-(--uizz-antd-color-border) px-3 py-1 break-all'
              >
                <Typography.Text>{file.name}</Typography.Text>
                <Button type='text' icon={<Icon.Trash2 size={15}  />} onClick={handleRemove} />
              </Flex>
            </>
          )}
          {!file && (
            <div
              id='drag-and-drop-plr'
              {...getRootProps({
                className:
                  'flex h-full cursor-pointer items-center justify-center rounded-xs border-2 border-dashed border-(--uizz-antd-color-border) bg-(--uizz-antd-color-fill-alter) p-6 text-center'
              })}
            >
              {uploading && (
                <Spin size='large'>
                  <Typography.Text>Por favor aguarde, estamos fazendo upload do arquivo.</Typography.Text>
                </Spin>
              )}
              {!uploading && (
                <>
                  <input {...getInputProps()} />
                  <Icon.CloudUpload size={24} className='mr-2.5 text-2xl'  />
                  <Typography.Text>
                    Selecione ou arraste seu arquivo de PLR (Direitos Privados de Etiqueta)
                  </Typography.Text>
                </>
              )}
            </div>
          )}
        </Col>
      </Row>
    </FormContent>
  );
};
